import { Box, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { IconDownloadOutline, IconFileOutline, IconVideoCameraOutline } from '@zillow/constellation-icons';
import { hasMissingAttachmentContent } from '@zg-rentals/satellite-components';
import { useContext, useEffect } from 'react';
import type { AttachmentsForMessage } from '../..';
import { logAttachmentsRenderError } from '../..';
import StringConstants from '../../../../constants/StringConstants';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import conversionUtils from '../../../../utils/conversionUtils';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import ExpiredAttachmentsText from '../ExpiredAttachmentsText';
import { StyledContent, StyledExpiredContent, StyledHorizontalCard } from './StyledComponents';
import { truncatedTextStyles } from '../../../../styles/text';
import {
  emitIreneDownloadAttachmentsEvent,
  IRENE_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/attachment';
import { useAuth } from '@zg-rentals/auth-comet';

type Props = {
  isMessageOwner: boolean;
  nonImageAttachments: AttachmentsForMessage;
};

const LOGGER_NAME = 'MessageAttachments_NonImageList';
const { IMPERSONATION_DETECTED } = StringConstants.ATTACHMENTS;

const NonImageList = ({ isMessageOwner, nonImageAttachments }: Props): JSX.Element => {
  const { conversationId } = useContext(InboxPageContext);
  const { user } = useAuth();
  let showExpiredText = false;
  let shouldLogAttachmentsRenderError = false;

  useEffect(() => {
    if (shouldLogAttachmentsRenderError) {
      logAttachmentsRenderError({ conversationId: conversationId || '', loggerName: LOGGER_NAME });
    }
  }, [shouldLogAttachmentsRenderError]);

  const handleEventTracking = () => {
    emitIreneDownloadAttachmentsEvent(user);
  };

  return (
    <>
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: isMessageOwner ? 'flex-end' : 'flex-start' }}>
        {nonImageAttachments.map((nonImageAttachment, index) => {
          const {
            attachmentMetadataStatus,
            downloadUrl,
            fileName,
            fileType,
            fileSizeBytes,
            isAttachmentExpired,
            zDocId,
          } = nonImageAttachment;

          const hasMissingContent = hasMissingAttachmentContent(attachmentMetadataStatus, isAttachmentExpired);
          const isImpersonator = downloadUrl === IMPERSONATION_DETECTED;
          const isDisabled = isAttachmentExpired || isImpersonator || hasMissingContent || downloadUrl === '';
          const fileSizeFormatted = fileSizeBytes ? conversionUtils.formatBytes(fileSizeBytes) : '';
          const isVideoFileType = fileType?.includes(StringConstants.ATTACHMENTS.VIDEO_FILE_TYPE);
          const key = zDocId ? `nonImageAttachment-${zDocId}` : `nonImageAttachment-${index}`;
          const ariaLabel = `Download ${fileName} ${fileSizeFormatted}`;

          if (!showExpiredText && isAttachmentExpired) showExpiredText = true;
          if (hasMissingContent) shouldLogAttachmentsRenderError = true;

          return (
            <TooltipWrapper key={key} tooltipPlacement="top-start" wrapInTooltip={isImpersonator}>
              <StyledHorizontalCard
                data-eventid={IRENE_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID}
                as={isDisabled ? 'button' : 'a'}
                aria-label={ariaLabel}
                aria-disabled={isDisabled}
                disabled={isDisabled}
                download={fileName}
                href={isDisabled ? undefined : downloadUrl}
                onClick={handleEventTracking}
                $isExpired={isAttachmentExpired}
                $isMessageOwner={isMessageOwner}
              >
                <Icon
                  css={{ color: isDisabled ? 'border.action.neutral.default' : 'icon.muted', marginRight: 'tighter' }}
                  size="lg"
                >
                  {isVideoFileType ? <IconVideoCameraOutline /> : <IconFileOutline />}
                </Icon>

                {isAttachmentExpired ? (
                  <StyledExpiredContent data-testid="attachment-expired-text">
                    <Paragraph
                      color="text.subtle"
                      textStyle="body-xs"
                      css={{ ...truncatedTextStyles, maxWidth: '140px' }}
                    >
                      {fileName}&nbsp;
                    </Paragraph>
                    <Text color="text.subtle" textStyle="body-xs">
                      has expired and can't be viewed.
                    </Text>
                  </StyledExpiredContent>
                ) : (
                  <>
                    {fileName ? (
                      <StyledContent>
                        <Paragraph
                          data-testid="attachment-file-name"
                          textStyle="body-sm-bold"
                          css={{ ...truncatedTextStyles, maxWidth: '140px' }}
                        >
                          {fileName}
                        </Paragraph>
                        <Paragraph data-testid="attachment-bytes" textStyle="body-xs">
                          {fileSizeFormatted}
                        </Paragraph>
                      </StyledContent>
                    ) : (
                      <Paragraph textStyle="body-xs" css={{ paddingRight: 'tighter' }}>
                        We're unable to display this file right now
                      </Paragraph>
                    )}

                    <Icon css={{ color: isDisabled ? 'border.action.neutral.default' : 'brand' }} size="md">
                      <IconDownloadOutline />
                    </Icon>
                  </>
                )}
              </StyledHorizontalCard>
            </TooltipWrapper>
          );
        })}
      </Box>

      {showExpiredText && (
        <ExpiredAttachmentsText isPlural={!!nonImageAttachments.length && nonImageAttachments.length > 1} />
      )}
    </>
  );
};

export default NonImageList;
